import { useState, useCallback, useEffect } from 'react';
import { Loader2, Lock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { BrandMark } from '../components/SiteChrome';
import { Panel, Kpi, DayBars, PopularReads, TopTickers, EnquiryList, LeadSources, MonthlyTable, PipelineChips } from '../components/admin/AdminWidgets';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KEY = 'tpmi_admin_pass';

const getJson = async (path, code, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'X-Admin-Passcode': code } });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.detail || 'Request failed');
  return d;
};

const PasscodeForm = ({ onSubmit, loading, error }) => {
  const [input, setInput] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (input) onSubmit(input); }} className="max-w-sm bg-zinc-950 border border-zinc-800 rounded-lg p-5 space-y-3" data-testid="admin-passcode-form">
      <label className="text-xs text-zinc-500 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Admin passcode</label>
      <input type="password" value={input} onChange={(e) => setInput(e.target.value)} autoComplete="current-password"
        className="w-full bg-black border border-zinc-800 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-brand/60" data-testid="admin-passcode-input" />
      {error && <p className="text-xs text-red-400" data-testid="admin-error">{error}</p>}
      <button type="submit" disabled={!input || loading} className="w-full bg-brand hover:bg-brand-light text-black font-bold text-sm py-2.5 rounded-md disabled:opacity-30 flex justify-center" data-testid="admin-unlock-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock'}
      </button>
    </form>
  );
};

const Dashboard = ({ data, enquiries, onDelete, onStatus }) => (
  <div className="space-y-5" data-testid="admin-dashboard">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi label="Analyses" value={data.week.analyses} prev={data.prev_week.analyses} sub={`${data.all_time.analyses.toLocaleString()} all time`} testId="kpi-analyses" />
      <Kpi label="Cards shared" value={data.week.cards_created} prev={data.prev_week.cards_created} sub={`${data.all_time.cards_created.toLocaleString()} all time`} testId="kpi-cards" />
      <Kpi label="Card opens" value={data.week.card_opens} prev={data.prev_week.card_opens} sub="unique device per day" testId="kpi-opens" />
      <Kpi label="Enquiries" value={data.week.enquiries} prev={data.prev_week.enquiries} sub={`${data.all_time.enquiries.toLocaleString()} all time`} testId="kpi-enquiries" />
    </div>
    <Panel title="Analyses · last 7 days" testId="panel-daily"><DayBars days={data.analyses_by_day} /></Panel>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <Panel title="Popular reads · top 10 this week" testId="panel-popular"><PopularReads rows={data.popular_reads} /></Panel>
      <Panel title="Most analysed tickers · this week" testId="panel-tickers"><TopTickers rows={data.top_tickers} /></Panel>
    </div>
    <Panel title={`Business enquiries · ${enquiries.length}`} testId="panel-enquiries">
      <PipelineChips pipeline={data.pipeline} />
      <LeadSources sources={data.lead_sources} />
      <EnquiryList items={enquiries} onDelete={onDelete} onStatus={onStatus} />
    </Panel>
    <Panel title="Monthly summary · last 6 months" testId="panel-monthly"><MonthlyTable rows={data.monthly} /></Panel>
  </div>
);

export default function AdminCentre() {
  const [pass, setPass] = useState(() => sessionStorage.getItem(KEY) || '');
  const [data, setData] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (code) => {
    setLoading(true); setError(null);
    try {
      const overview = await getJson('/api/admin/overview', code);
      const enq = await getJson('/api/admin/enquiries', code);
      sessionStorage.setItem(KEY, code); setPass(code); setData(overview); setEnquiries(enq.enquiries);
    } catch (e) { sessionStorage.removeItem(KEY); setPass(''); setData(null); setError(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (pass) load(pass); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    try {
      await getJson(`/api/admin/enquiries/${id}`, pass, { method: 'DELETE' });
      toast.success('Enquiry deleted'); load(pass);
    } catch (e) { toast.error(e.message || 'Delete failed. Please try again.'); }
  };

  const setStatus = async (id, status) => {
    setEnquiries((it) => it.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await getJson(`/api/admin/enquiries/${id}`, pass, { method: 'PATCH', body: JSON.stringify({ status }) });
      const overview = await getJson('/api/admin/overview', pass);
      setData(overview);
    } catch (e) { toast.error(e.message || 'Status update failed'); load(pass); }
  };

  const lock = () => { sessionStorage.removeItem(KEY); setPass(''); setData(null); setEnquiries([]); };

  return (
    <div className="min-h-screen bg-black">
      <nav className="border-b border-zinc-800/60"><div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
        <BrandMark />
        {data && (
          <div className="flex items-center gap-4">
            <button onClick={() => load(pass)} disabled={loading} className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1.5" data-testid="admin-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={lock} className="text-xs text-zinc-500 hover:text-white" data-testid="admin-signout-btn">Lock</button>
          </div>
        )}
      </div></nav>
      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-1" data-testid="admin-title">Admin centre</h1>
        <p className="text-xs text-zinc-500 mb-6">Live usage and business leads for Trade PMI. Weekly figures cover the last 7 days (UTC).</p>
        {data ? <Dashboard data={data} enquiries={enquiries} onDelete={remove} onStatus={setStatus} /> : <PasscodeForm onSubmit={load} loading={loading} error={error} />}
      </main>
    </div>
  );
}
